import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { Role, RolesDescriptor } from '@/auth/azure/Roles'
import { DocumentPermissions } from '@/enums'
import { Crumb, OptionalLoaderGeneric, TypedMatch } from '@/types'
import { DataGridRef } from 'devextreme-react/cjs/data-grid'
import type { DateBoxTypes } from 'devextreme-react/cjs/date-box'
import { TextEditorButton } from 'devextreme/common'
import ODataStore from 'devextreme/data/odata/store'
import { ClickEvent } from 'devextreme/ui/button'
import { DropDownPredefinedButton } from 'devextreme/ui/drop_down_editor/ui.drop_down_editor'
import notify from 'devextreme/ui/notify'

export const toolbarHtmlEditor = (screenSize?: {
  isXSmall: boolean
  isSmall: boolean
  isMedium: boolean
  isLarge: boolean
}) => {
  return {
    container: null,
    items: [
      'undo',
      'redo',
      'separator',
      {
        name: 'header',
        acceptedValues: [1, 2, 3, false],
      },
      'separator',
      'bold',
      'italic',
      'strike',
      'underline',
      'separator',
      'alignLeft',
      'alignCenter',
      'alignRight',
      'alignJustify',
      'separator',
      'orderedList',
      'bulletList',
      'separator',
      'color',
      'background',
      'separator',
      'link',
      'image',
      'separator',
      'clear',
      'codeBlock',
      'blockquote',
      'separator',
      'insertTable',
      'deleteTable',
      'insertRowAbove',
      'insertRowBelow',
      'deleteRow',
      'insertColumnLeft',
      'insertColumnRight',
      'deleteColumn',
    ],
    multiline: screenSize?.isLarge || screenSize?.isMedium,
  }
}

export const columnSourceFactory = (
  token: string | undefined,
  entity: string,
  dataField: string,
  sortDesc?: boolean,
  filter?: any[],
) => {
  return {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/${entity}`,
      // key: 'id',
      // keyType: 'Int32',
      version: 4,
      errorHandler: (e) => {
        console.error(e.errorDetails)
        if (!e.errorDetails) return
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
    }),
    group: { selector: dataField, desc: true },
    sort: sortDesc === undefined ? [`${dataField}`] : undefined,
    select: ['id', `${dataField}`],
    filter: filter,
    postProcess: (results: any[]) => {
      console.log('results', results)
      const resultsMap: { text: any; value: any }[] = []
      results.map((itemResult: any) => {
        //array di oggetti del tipo richiesto dal headerFilter
        resultsMap.push({ text: itemResult.key, value: itemResult.key })
      })
      resultsMap.push({ text: '(Blanks)', value: null })
      return resultsMap
    },
  }
}

export const isUserRoleAllowed = (
  userRoles: RolesDescriptor | undefined,
  allowedRoles: Role[] | undefined,
): boolean => {
  if (allowedRoles) return allowedRoles.some((role: Role) => userRoles?.hasRole(role))
  return false
}

export const customButton = (name: string, onClick?: (e: ClickEvent) => void, disabled = false, hint = '') => {
  const button: DropDownPredefinedButton | TextEditorButton = {
    name,
    location: 'after',
    options: {
      disabled,
      visible: true,
      stylingMode: 'text',
      icon: name === 'open' ? 'folder' : name === 'call' ? 'tel' : name,
      hint:
        hint === ''
          ? name === 'open'
            ? 'Apri'
            : name === 'add'
              ? 'Aggiungi'
              : name === 'info'
                ? 'Info'
                : name === 'email'
                  ? 'Email'
                  : name === 'call'
                    ? 'Chiama'
                    : name === 'find'
                      ? 'Cerca'
                      : name === 'clear'
                        ? 'Cancella'
                        : ''
          : hint,
      onClick,
    },
  }
  return button
}

export const createTime = (hours: number, minutes: number, year = 1970, month = 0, day = 1) =>
  new Date(year, month, day, hours, minutes, 0, 0)

export const parseTypedTime = (text: string, year = 1970, month = 0, day = 1): Date | null => {
  const trimmed = text.trim()
  if (!trimmed) return null

  let hours: number
  let minutes: number
  const separated = trimmed.match(/^(\d{1,2})[:.,](\d{0,2})$/)
  if (separated) {
    hours = Number(separated[1])
    minutes = separated[2] ? Number(separated[2].padEnd(2, '0')) : 0
  } else {
    const digits = trimmed
    if (!/^\d{1,4}$/.test(digits)) return null

    if (digits.length <= 2) {
      hours = Number(digits)
      minutes = 0
    } else if (digits.length === 3) {
      hours = Number(digits.slice(0, 1))
      minutes = Number(digits.slice(1))
    } else {
      hours = Number(digits.slice(0, 2))
      minutes = Number(digits.slice(2))
    }
  }

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null
  return createTime(hours, minutes, year, month, day)
}

export const completeDateBoxTimeOnTab = (
  e: DateBoxTypes.KeyDownEvent,
  year = 1970,
  month = 0,
  day = 1,
): Date | null => {
  if (e.event?.key !== 'Tab') return null

  const input = e.event.target instanceof HTMLInputElement ? e.event.target : null
  const parsedTime = parseTypedTime(input?.value ?? e.component.option('text') ?? '', year, month, day)
  if (!parsedTime) return null

  e.component.option('value', parsedTime)
  return parsedTime
}

export const customDisplayExpr = (item: any) => {
  return item
    ? `${item.ded_Dis}${item.sede?.azienda?.nome ? ' - ' : ''}${item.sede?.azienda?.nome ?? ''}${item.sede?.nome ? ' - ' : ''}${item.sede?.nome ?? ''}`
    : ``
}

export const getRowIndexByKey = (gridRef: React.RefObject<DataGridRef>) => {
  const rowIndexByKey = gridRef.current
    ?.instance()
    .getRowIndexByKey(gridRef.current?.instance().option('focusedRowKey'))
  console.log('rowIndexByKey', rowIndexByKey)
  return rowIndexByKey
}

export const capitalizeString = (text: string | undefined) => {
  return text!.charAt(0).toUpperCase() + text!.slice(1)
}

export const onDataGridSelectedRowButtonClick = (
  gridRef: React.RefObject<DataGridRef>,
  buttonAction: (rowData: any) => void,
) => {
  const rowIndexByKey = getRowIndexByKey(gridRef)
  if (rowIndexByKey === -1) notify(`Nessuna riga selezionata, impossibile procedere.`, 'warning', 2000)
  else {
    gridRef.current
      ?.instance()
      .byKey(gridRef.current.instance().option().focusedRowKey)
      .then((rowdata: any) => {
        buttonAction(rowdata)
      })
  }
}

export const onValueChangedHtmlEditor = (e: {
  event: { type: string; originalEvent: { clipboardData: { getData: (format: string) => string } } }
  component: {
    getSelection: () => { index: number }
    setSelection: (index: number, length: number) => void
    format: (type: string, options: { href: string }) => void
    insertText: (index: number, text: string) => void
  }
}) => {
  if (e.event && e.event.type === 'paste') {
    console.log('e', e)
    const txt: string = e.event.originalEvent.clipboardData.getData('text')
    console.log('txt', txt)

    // Regex to match URLs (http, https, and www)
    const urlRegex = /(https?:\/\/\S+)|(www\.\S+)/gi
    const urls = txt.match(urlRegex)

    if (urls && urls.length > 0) {
      const startIndex: number = e.component.getSelection().index

      setTimeout(() => {
        let currentIndex = startIndex
        let lastProcessedIndex = 0

        // Process each URL found in the text
        urls.forEach((url) => {
          const urlPosition = txt.indexOf(url, lastProcessedIndex)

          if (urlPosition !== -1) {
            // Calculate the position in the editor
            const offset = urlPosition - lastProcessedIndex
            currentIndex += offset

            // Select and format the URL
            e.component.setSelection(currentIndex, url.length)
            e.component.format('link', {
              href: url.startsWith('www') ? `https://${url}` : url,
            })

            // Move to the next position
            currentIndex += url.length
            lastProcessedIndex = urlPosition + url.length
          }
        })

        // Set cursor at the end
        e.component.setSelection(startIndex + txt.length, 0)
      }, 0)
    }
  }
}

export const onInitializedHtmlEditor = (e: any) => {
  const editor: any = e.component
  const Link: any = editor.get('formats/link')

  class ClickableLink extends Link {
    static create(value: string): HTMLElement {
      const node: HTMLElement = super.create(value)
      console.log(node)
      node.setAttribute('contenteditable', 'false')

      return node
    }
  }

  editor.register({ 'formats/link': ClickableLink })
}

export const setDrivePermissions = async (url: string, objectId: number, path: string) => {
  const client = useQsAdminApiManager.getState().client
  try {
    const response = await client.post(
      url,
      {
        objectId,
        path,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
    return response.status === 200 ? (response.data as DocumentPermissions) : DocumentPermissions.Deny
  } catch (error) {
    console.log(`Errore in settaggio permessi sulla cartella ${path}`, error)
    return DocumentPermissions.Deny
  }
}

/**
 * Gets a value from a nested object using a string path.
 * Safely handles undefined values and array indices.
 * The key improvement is to ensure we never pass undefined to any conversion functions.
 *
 * @param obj The object to retrieve the value from
 * @param path The path to the property using dot notation (e.g., 'user.address.street')
 * @param defaultValue Optional default value to return if the path doesn't exist
 * @returns The property value or defaultValue if any part of the path is invalid
 */
export function readProperty(obj: any, path: string, defaultValue?: any) {
  if (obj === null || obj === undefined || path === null || path === undefined) {
    return defaultValue
  }

  try {
    const value = path.split('.').reduce((acc, part) => {
      // Return early if we've already hit undefined or null
      if (acc === null || acc === undefined) {
        return undefined
      }

      // Handle array indexing pattern like 'items[0]'
      const match = part.match(/^([^[]+)\[(\d+)]$/)
      if (match) {
        const [_, key, indexStr] = match
        const index = Number(indexStr)

        // Check if key exists
        if (!(key in acc)) {
          return undefined
        }

        const array = acc[key]
        // Check if it's an array and the index is valid
        if (!Array.isArray(array) || index < 0 || index >= array.length) {
          return undefined
        }

        return array[index]
      }

      // Regular property access
      // Only access properties that actually exist on the object to avoid
      // potential "undefined" conversion errors
      return Object.prototype.hasOwnProperty.call(acc, part) ? acc[part] : undefined
    }, obj)

    // Use the default value for undefined, null, or NaN
    if (value === undefined || value === null || (typeof value === 'number' && isNaN(value))) {
      return defaultValue
    }

    return value
  } catch (error) {
    // In case of any unexpected errors in the path traversal, return the default value
    console.warn(`Error in readProperty for path "${path}":`, error)
    return defaultValue
  }
}

export const htmlToPlainText = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

export const calculateDateRangeFilterValue = (value: any) => {
  const now: Date = new Date()
  let minDate: Date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
  let maxDate: Date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0)
  switch (value) {
    case 'Oggi': {
      minDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
      break
    }
    case 'Ultima settimana': {
      minDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0)
      break
    }
    case 'Settimana corrente': {
      const day = now.getDay()
      const diffToMonday = day === 0 ? -6 : 1 - day
      minDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMonday, 0, 0, 0)
      maxDate = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate() + 7, 0, 0, 0)
      break
    }
    case 'Ultimo mese': {
      minDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate(), 0, 0, 0)
      break
    }
    case 'Ultimi 3 mesi': {
      minDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate(), 0, 0, 0)
      break
    }
    case 'Ultimi 6 mesi': {
      minDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate(), 0, 0, 0)
      break
    }
    case 'Ultimi 12 mesi': {
      minDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate(), 0, 0, 0)
      break
    }
    case 'Ultimo mese solare': {
      minDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0)
      maxDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0)
      break
    }
    case 'Mese corrente': {
      minDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)
      maxDate = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0)
      break
    }
    case 'Anno corrente': {
      minDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0)
      maxDate = new Date(now.getFullYear() + 1, 0, 1, 0, 0, 0)
      break
    }
  }
  return { minDate, maxDate }
}

// Type guard for checking if a match has a crumb function
// This is necessary for type safety when accessing the crumb function
export const hasCrumb = <T extends OptionalLoaderGeneric>(
  match: TypedMatch<T>,
): match is TypedMatch<T> & { handle: { crumb: Crumb<T> } } => {
  // this guarantees that the match has a crumb function
  return Boolean(match.handle?.crumb)
}
